import { ChevronRight, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge, type StatusTone } from "@/components/status-badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDate, formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"
import type {
  DeliveryFormSummary,
  DeliveryLoadEmbed,
  OrderDetail,
} from "@/types/domain"

type DeliveryStage = "PENDING" | "SCALE" | "QUALITY" | "ACCEPTED" | "REJECTED"

function firstLoad(d: DeliveryFormSummary): DeliveryLoadEmbed | undefined {
  return d.ExporterLoads?.[0] ?? undefined
}

function stageFor(d: DeliveryFormSummary): DeliveryStage {
  const load = firstLoad(d)
  if (!load) return "PENDING"
  if (load.status === "COMPLETED") {
    return load.decision === "REJECTED" ? "REJECTED" : "ACCEPTED"
  }
  return load.status ?? "PENDING"
}

function stageTone(stage: DeliveryStage): StatusTone {
  switch (stage) {
    case "PENDING":
      return "warning"
    case "SCALE":
    case "QUALITY":
      return "info"
    case "ACCEPTED":
      return "success"
    case "REJECTED":
      return "danger"
  }
}

interface OrderDeliveriesTableProps {
  order: OrderDetail
}

export function OrderDeliveriesTable({ order }: OrderDeliveriesTableProps) {
  const { t, i18n } = useTranslation(["orders", "common"])
  const navigate = useNavigate()

  const items = (order.DeliveryForms ?? []).filter((d) => d.isLocked)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Truck className="text-muted-foreground size-4" />
          {t("deliveriesTable.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState
            title={t("deliveriesTable.empty")}
            className="border-0 py-10"
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("deliveriesTable.bol")}</TableHead>
                  <TableHead>{t("deliveriesTable.truck")}</TableHead>
                  <TableHead>{t("deliveriesTable.date")}</TableHead>
                  <TableHead className="text-right">
                    {t("deliveriesTable.quantity")}
                  </TableHead>
                  <TableHead>{t("deliveriesTable.stage")}</TableHead>
                  <TableHead aria-hidden className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => {
                  const load = firstLoad(d)
                  const stage = stageFor(d)
                  const target = load
                    ? paths.receptionDetail(load.id)
                    : null
                  return (
                    <TableRow
                      key={String(d.id)}
                      className={target ? "cursor-pointer" : ""}
                      onClick={() => {
                        if (target) navigate(target)
                      }}
                    >
                      <TableCell className="font-medium">
                        {d.billOfLadingNumber ?? `#${d.id}`}
                      </TableCell>
                      <TableCell>
                        {d.truckRegistrationNumber ?? "—"}
                      </TableCell>
                      <TableCell>
                        {formatDate(d.created_at, i18n.language) || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(kgToMt(d.netKilogramsAttached ?? 0), 2)}{" "}
                        {t("units.mt", { ns: "common" })}
                      </TableCell>
                      <TableCell>
                        <StatusBadge tone={stageTone(stage)}>
                          {t(`deliveriesTable.stages.${stage}`)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {target ? (
                          <ChevronRight className="text-muted-foreground size-4" />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
